'use client';

/**
 * DetailPage scaffold — Phase A2.
 *
 * Two-column body wrapper that every detail page should render through.
 * The hero header sits on top, the body is a `1fr + 320px` grid (main
 * content + sticky right rail) that collapses to a single column below
 * `lg` (1024px).
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A2. Reference implementation is the
 * Phase 5 incident-detail v2 (`apps/web/src/components/nav-v2/
 * incident-detail/incident-detail-v2.tsx`) — moving its layout primitives
 * into the design system so client / care-plan / staff detail pages
 * can compose the same shape.
 *
 * Owned-by-scaffold:
 * - Outer spacing + responsive grid
 * - Hero header (via DetailPageHeader)
 * - Sticky bottom action bar slot (appears when in edit mode)
 *
 * Owned-by-page:
 * - Hero content (title, eyebrow, status pills, actions)
 * - Body sections (tabs, cards, forms, timelines)
 * - Right-rail content (details panel, assignment, related records, audit)
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { ExperienceRecordEvidence } from '../experience-record-evidence';
import { DetailPageHeader, type DetailPageHeaderProps } from './detail-page-header';

export interface DetailPageProps extends Omit<DetailPageHeaderProps, 'className'> {
  /** Main body content (left column on desktop, full-width on mobile). */
  children: React.ReactNode;
  /** Optional right rail content (sticky on desktop, stacks below on mobile). */
  rail?: React.ReactNode;
  /** Width of the right rail. Defaults to 320px. */
  railWidth?: number;
  /** Sticky bottom action bar — appears only when this prop is non-null. */
  stickyBottomBar?: React.ReactNode;
  /** Override the outer wrapper class. */
  className?: string;
  /** Override the default header border + background. */
  headerClassName?: string;
  /** Exact rendered record/relationship/outcome evidence for provider-runtime certification. */
  experienceEvidence?: {
    entityIds: readonly string[];
    relationshipIds?: readonly string[];
    outcomeTokens?: readonly string[];
    revision: string;
  };
}

export function DetailPage({
  // header
  backHref,
  backLabel,
  backLinkRender,
  backLinkClassName,
  eyebrow,
  title,
  statusPills,
  actions,
  banner,
  presence,
  share,
  // body
  children,
  rail,
  railWidth = 320,
  stickyBottomBar,
  className,
  headerClassName,
  experienceEvidence,
}: DetailPageProps) {
  return (
    <div className={cn('flex flex-col gap-4 pb-16', className)} data-detail-page>
      {experienceEvidence ? <ExperienceRecordEvidence {...experienceEvidence} /> : null}
      <DetailPageHeader
        backHref={backHref}
        backLabel={backLabel}
        backLinkRender={backLinkRender}
        backLinkClassName={backLinkClassName}
        eyebrow={eyebrow}
        title={title}
        statusPills={statusPills}
        actions={actions}
        banner={banner}
        presence={presence}
        share={share}
        className={headerClassName}
      />

      {rail ? (
        <div
          className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_var(--detail-rail-width)]"
          style={{ ['--detail-rail-width' as string]: `${railWidth}px` }}
        >
          <div className="min-w-0 space-y-4">{children}</div>
          <aside
            className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:self-start"
            aria-label="Right rail"
          >
            {rail}
          </aside>
        </div>
      ) : (
        <div className="min-w-0 space-y-4">{children}</div>
      )}

      {stickyBottomBar && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur sm:px-6"
          role="region"
          aria-label="Page actions"
        >
          <div className="min-w-0 max-w-full overflow-x-auto">{stickyBottomBar}</div>
        </div>
      )}
    </div>
  );
}
